/**
 * Vorlagen für fertige Schulfächer (Name, Icon, Farbe, Fachbereich).
 *
 * Genutzt vom Onboarding („Schnellstart") und von den Einstellungen
 * („Fächer aus Vorlage"). Jedes Fach trägt mehrere Tags (Fachbereich +
 * Haupt-/Nebenfach), nach denen sich die Standard-Überschriften wählen lassen.
 */
import { h, mount } from "@/lib/dom";
import { icon } from "@/lib/icons";
import { subjectIcon } from "@/lib/subjectIcons";
import { contrastText } from "@/lib/colors";
import { subjectsService, pbErrorMessage } from "@/services";
import { toast } from "@/components/ui";

export interface PresetSubject {
  title: string;
  icon: string; // Name aus subjectIcons.ts
  color: string;
  bereich: string; // Fachbereich (Sprachen, MINT …)
  haupt: boolean; // Hauptfach (sonst Nebenfach)
}

// Orientiert an den in Schleswig-Holstein gebräuchlichen Fachnamen
// (z. B. „Wirtschaft/Politik" statt „Sozialkunde"), bewusst kompakt gehalten.
export const SUBJECT_PRESETS: PresetSubject[] = [
  // Sprachen
  { title: "Deutsch",       icon: "message",    color: "#dc2626", bereich: "Sprachen", haupt: true },
  { title: "Englisch",      icon: "languages",  color: "#2563eb", bereich: "Sprachen", haupt: true },
  { title: "Französisch",   icon: "languages",  color: "#7c3aed", bereich: "Sprachen", haupt: false },
  { title: "Latein",        icon: "scroll",     color: "#78350f", bereich: "Sprachen", haupt: false },
  { title: "Spanisch",      icon: "languages",  color: "#ea580c", bereich: "Sprachen", haupt: false },
  // MINT
  { title: "Mathematik",    icon: "calculator", color: "#2563eb", bereich: "MINT", haupt: true },
  { title: "Biologie",      icon: "dna",        color: "#16a34a", bereich: "MINT", haupt: false },
  { title: "Chemie",        icon: "flask",      color: "#059669", bereich: "MINT", haupt: false },
  { title: "Physik",        icon: "atom",       color: "#4f46e5", bereich: "MINT", haupt: false },
  { title: "Informatik",    icon: "code",       color: "#3f3f46", bereich: "MINT", haupt: false },
  // Gesellschaft
  { title: "Geschichte",         icon: "scroll",   color: "#d97706", bereich: "Gesellschaft", haupt: false },
  { title: "Erdkunde",           icon: "globe",    color: "#14b8a6", bereich: "Gesellschaft", haupt: false },
  { title: "Wirtschaft/Politik", icon: "landmark", color: "#a3123a", bereich: "Gesellschaft", haupt: false },
  { title: "Religion",           icon: "heart",    color: "#c026d3", bereich: "Gesellschaft", haupt: false },
  { title: "Philosophie",        icon: "lightbulb", color: "#7c3aed", bereich: "Gesellschaft", haupt: false },
  // Künste & Sport
  { title: "Kunst",              icon: "palette",  color: "#ec4899", bereich: "Künste & Sport", haupt: false },
  { title: "Musik",              icon: "music",    color: "#ca8a04", bereich: "Künste & Sport", haupt: false },
  { title: "Darstellendes Spiel", icon: "drama",   color: "#9333ea", bereich: "Künste & Sport", haupt: false },
  { title: "Sport",              icon: "activity", color: "#84cc16", bereich: "Künste & Sport", haupt: false },
];

/** Sortier-/Überschriften-Optionen. `cat` liefert die Kategorie eines Fachs. */
export type SortMode = "bereich" | "stellenwert" | "alpha";

export const SORT_MODES: {
  key: SortMode;
  label: string;
  cat: (p: PresetSubject) => string;
}[] = [
  { key: "bereich",     label: "Nach Fachbereich", cat: (p) => p.bereich },
  { key: "stellenwert", label: "Haupt-/Nebenfach", cat: (p) => (p.haupt ? "Hauptfächer" : "Nebenfächer") },
  { key: "alpha",       label: "Alphabetisch",     cat: () => "" },
];

/** Vergleichsschlüssel für „gibt es dieses Fach schon?". */
export function normalizeTitle(title: string): string {
  return title.trim().toLowerCase().replace(/\s+/g, " ");
}

export interface PresetPickerOptions {
  /** Titel bereits vorhandener Fächer – werden ausgegraut und bleiben gewählt. */
  existing?: Iterable<string>;
  /** Startwert der Gruppierung. */
  sortMode?: SortMode;
  /** Feuert bei jeder Änderung der Auswahl. */
  onChange?: (selectedCount: number) => void;
}

export interface PresetPicker {
  el: HTMLElement;
  /** Nur die neu gewählten Fächer (ohne die bereits vorhandenen). */
  getSelected: () => PresetSubject[];
  /** Kategorie eines Fachs gemäß aktuell gewählter Gruppierung. */
  categoryOf: (p: PresetSubject) => string;
}

/**
 * Auswahl-Grid für Vorlagen-Fächer. Bereits angelegte Fächer erscheinen
 * ausgegraut mit gesetztem Haken und lassen sich nicht abwählen.
 */
export function presetPicker(opts: PresetPickerOptions = {}): PresetPicker {
  const existing = new Set([...(opts.existing ?? [])].map(normalizeTitle));
  const selected = new Set<string>();
  let sortMode: SortMode = opts.sortMode ?? "bereich";
  const listEl = h("div", null);

  const curSort = () => SORT_MODES.find((s) => s.key === sortMode) ?? SORT_MODES[0];
  const notify = () => opts.onChange?.(selected.size);

  function renderList() {
    const sort = curSort();
    // Gruppen in Reihenfolge des ersten Vorkommens; alphabetisch = eine Gruppe
    const groups =
      sortMode === "alpha" ? [""] : [...new Set(SUBJECT_PRESETS.map((p) => sort.cat(p)))];
    const presets =
      sortMode === "alpha"
        ? [...SUBJECT_PRESETS].sort((a, b) => a.title.localeCompare(b.title, "de"))
        : SUBJECT_PRESETS;

    mount(
      listEl,
      h(
        "div",
        null,
        ...groups.flatMap((cat) => [
          cat ? h("div", { class: "ob-cat" }, cat) : null,
          h(
            "div",
            { class: "ob-subjects" },
            ...presets
              .filter((p) => sort.cat(p) === cat)
              .map((p) => {
                const already = existing.has(normalizeTitle(p.title));
                const card = h(
                  "button",
                  {
                    class:
                      "ob-subject" +
                      (already ? " selected is-existing" : selected.has(p.title) ? " selected" : ""),
                    type: "button",
                    disabled: already,
                    title: already ? "Bereits angelegt" : `„${p.title}" auswählen`,
                    onclick: () => {
                      if (already) return;
                      if (selected.has(p.title)) selected.delete(p.title);
                      else selected.add(p.title);
                      card.classList.toggle("selected", selected.has(p.title));
                      notify();
                    },
                  },
                  h("span", {
                    class: "ob-icon",
                    style: `background:${p.color};color:${contrastText(p.color)}`,
                    html: subjectIcon(p.icon, 16),
                  }),
                  h("span", { class: "ob-name" }, p.title),
                  h("span", { class: "ob-check", html: icon("check", 12) })
                );
                return card;
              })
          ),
        ])
      )
    );
  }

  const sortBtns = SORT_MODES.map((s) =>
    h(
      "button",
      {
        type: "button",
        class: sortMode === s.key ? "active" : "",
        onclick: () => {
          sortMode = s.key;
          sortBtns.forEach((b, i) => b.classList.toggle("active", SORT_MODES[i].key === sortMode));
          renderList();
        },
      },
      s.label
    )
  );

  renderList();

  return {
    el: h(
      "div",
      null,
      h("div", { class: "segmented segmented-wrap" }, ...sortBtns),
      listEl
    ),
    getSelected: () => SUBJECT_PRESETS.filter((p) => selected.has(p.title)),
    categoryOf: (p) => curSort().cat(p),
  };
}

/**
 * Legt die gewählten Vorlagen-Fächer an (Kategorie = gewählte Gruppierung).
 * Fehler pro Fach werden gemeldet, der Rest wird trotzdem angelegt.
 * Rückgabe: Anzahl tatsächlich angelegter Fächer.
 */
export async function createPresetSubjects(
  picker: PresetPicker,
  startPosition = 0
): Promise<number> {
  const chosen = picker.getSelected();
  let created = 0;
  for (let i = 0; i < chosen.length; i++) {
    const p = chosen[i];
    try {
      await subjectsService.create({
        title: p.title,
        color: p.color,
        icon: p.icon,
        position: startPosition + i,
        category: picker.categoryOf(p),
      });
      created++;
    } catch (err) {
      toast(`${p.title}: ${pbErrorMessage(err)}`, "error");
    }
  }
  return created;
}
